import React, { useMemo, useRef, useState } from "react"

import { useQuery } from "@apollo/client"
import { SignOutButton } from "@clerk/clerk-react"
import CircularProgress from "@mui/material/CircularProgress"
import Stack from "@mui/material/Stack"
import Typography from "@mui/material/Typography"

import OrgSwitcher from "@components/OrgSwitcher"
import { useAuth } from "@components/auth"
import { logger } from "@helpers/logger"
import { unsafeCast } from "@utils/null"

import ls, { localKeys } from "@services/localStorage"

import { FetchSessionDocument, Language, ViewMode, Address } from "@gql/graphql"

import { neutral } from "@constants/colors"

import SessionContext, {
  RoleDetails,
  SessionContextType,
} from "./SessionContext"

interface SessionProviderProps {
  children: React.ReactNode
}

const SessionProvider: React.FC<SessionProviderProps> = ({
  children,
}: SessionProviderProps) => {
  const { isSignedIn, isLoaded, hasOrg } = useAuth()
  const [showOrgSwitcher, setShowOrgSwitcher] = useState(false)
  const showOrgSwitcherTimeout = useRef<NodeJS.Timeout | null>(null)

  const { data, startPolling, stopPolling, refetch } = useQuery(
    FetchSessionDocument,
    {
      skip: !isSignedIn || !isLoaded || !hasOrg,
      onCompleted: (data) => {
        if (!data?.organizationMember?.settings) {
          return
        }
        const currentLanguage = ls.getItem(localKeys.LANGUAGE)
        const currentViewMode = ls.getItem(localKeys.VIEW_MODE)

        const languagePreference =
          data?.organizationMember?.settings?.language ?? Language.En
        const viewModePreference =
          data?.organizationMember?.settings?.viewMode ?? ViewMode.Light

        if (currentLanguage !== languagePreference) {
          ls.setItem(localKeys.LANGUAGE, languagePreference)
          window.location.reload()
        }

        if (currentViewMode !== viewModePreference) {
          ls.setItem(localKeys.VIEW_MODE, viewModePreference)
          window.location.reload()
        }
      },
      onError: (e) => {
        logger.error("could not fetch this org's data", e)
      },
    },
  )

  const values = useMemo(() => {
    if (!data || !isSignedIn || !isLoaded) {
      return null
    }

    const newValues: SessionContextType = {
      currentUser:
        data.currentUser == null
          ? data.currentUser
          : {
              ...data.currentUser,
              id: data.currentUser?.id,
              username: "",
            },
      currentPermissions: new Set(
        data.currentPermissions?.map((permission) => permission.key),
      ),
      refetchSession: refetch,
      org:
        data.organization == null
          ? data.organization
          : {
              ...data.organization,
              logo: data.organization.logo ?? null,
              addresses: unsafeCast<Address[]>(data.organization.addresses),
            },
      orgMember:
        !hasOrg || !data.organizationMember
          ? null
          : {
              ...data.organizationMember,
              profile: {
                ...data.organizationMember.profile,
                status: data.organizationMember.profile.status ?? null,
                role: unsafeCast<RoleDetails>(
                  data.organizationMember.profile.role,
                ),
                about: data.organizationMember.profile.about ?? null,
                email: data.organizationMember.profile.email ?? "",
                image: data.organizationMember.profile.image ?? "",
                fullName: data.organizationMember.profile.fullName ?? "",
                firstName: data.organizationMember.profile.firstName ?? "",
                lastName: data.organizationMember.profile.lastName ?? "",
                firstLogin: data.organizationMember.profile?.firstLogin,
              },
              settings: {
                ...data.organizationMember.settings,
                viewMode:
                  data.organizationMember.settings?.viewMode ??
                  String(ViewMode.Dark),
                language:
                  data.organizationMember.settings?.language ??
                  String(Language.En),
                starredFeatures: {
                  edges:
                    data.organizationMember.settings?.starredFeatures?.edges?.map(
                      (edge) => ({
                        node: {
                          id: edge?.node?.id ?? "",
                          name: edge?.node?.name ?? "",
                        },
                      }),
                    ) ?? [],
                },
              },
            },
    }
    return newValues
  }, [data, refetch, isSignedIn, isLoaded, hasOrg])

  if (!isLoaded || (isSignedIn && !values && hasOrg)) {
    startPolling(1000)
    if (!showOrgSwitcherTimeout.current) {
      showOrgSwitcherTimeout.current = setTimeout(() => {
        setShowOrgSwitcher(true)
      }, 60000)
    }
    return (
      <>
        <Stack alignItems="center" justifyContent="center" height="100vh">
          <CircularProgress color="inherit" size={16} />
          <Typography variant="caption" marginTop={1} color={neutral[400]}>
            Just a moment...
          </Typography>
        </Stack>
        {showOrgSwitcher && (
          <>
            <Stack position="absolute" top="1vh" right="1vh">
              <SignOutButton
                // @ts-expect-error ClerkJS has wrong prop types, this works
                style={{
                  margin: 15,
                  padding: 5,
                  backgroundColor: "transparent",
                  color: neutral[400],
                  border: 0,
                }}
              />
            </Stack>
            <Stack position="absolute" bottom="2vh" right="0vh">
              <OrgSwitcher />
            </Stack>
          </>
        )}
      </>
    )
  }

  if (showOrgSwitcherTimeout.current) {
    clearTimeout(showOrgSwitcherTimeout.current)
    showOrgSwitcherTimeout.current = null
  }

  const finalValues = values ?? {
    currentUser: null,
    currentPermissions: null,
    org: null,
    orgMember: null,
    refetchSession: refetch,
    canViewReporting: false,
    canViewWorkspaceSettings: false,
    canViewWorkspacePeople: false,
  }

  stopPolling()
  return (
    <SessionContext.Provider value={finalValues}>
      {children}
    </SessionContext.Provider>
  )
}

export default SessionProvider
